import React, { useCallback, useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, Check } from 'react-feather';
import { Accordion } from 'semantic-ui-react';
import { INSURANCE_TYPES_ICONS_SVG_V2 } from 'constants/insurance';
import { limitNumber, prettifyNumber } from 'helpers';
import styles from '../styles/lifeCovers.scss';
import { HelpTooltip } from 'components/tooltip';
import { getEmployerPolicyGrouped } from '../utils';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';

export const Value = ({ label, tooltip = '', value }) => {
  if (!value) {
    return null;
  }

  return (
    <div className={styles.value}>
      <h3>{value}</h3>
      <label>
        {label}
        {tooltip && (
          <HelpTooltip
            toggleClassName={styles.tooltip}
            content={tooltip}
            position="right center"
          />
        )}
      </label>
    </div>
  );
};

const getTooltip = (policyType, employerPolicyDetails) => {
  const tooltip = R.path([policyType, 'coverTooltip'], employerPolicyDetails);
  if (tooltip) {
    return tooltip.map((t, idx) => <div key={`${policyType}_${idx}`}>{t}</div>);
  }
  return null;
};

const getCoverTooltip = (key, policyTypeDetails) => {
  return R.path([key, 'tooltip'], policyTypeDetails);
};

const renderLifeInsured = (insured, employerPolicies) => {
  return insured.map(({ name, additionalCovers = [], premiumCover }, i) => {
    const sortedCovers = R.sortBy(R.prop('policyType'), additionalCovers);

    return (
      <div key={`${name}_${i}`} className={styles.insured}>
        <div className={styles.insuredName}>
          <span>
            <b>Covers</b> - {name}
          </span>
          {premiumCover && (
            <div className={styles.premium}>
              <Check size={16} strokeWidth={3} />
              Premium cover
            </div>
          )}
        </div>
        <div className={styles.insuredCovers}>
          {sortedCovers.map((cover) => {
            const { policyType } = cover;

            const { policyDetails, disclaimer, limit } =
              employerPolicies[policyType] || {};

            const Icon = INSURANCE_TYPES_ICONS_SVG_V2[policyType];

            const tooltip = getTooltip(policyType, policyDetails);

            const {
              excess,
              insuredAmount,
              payoutFrequency,
              payoutWaitPeriod,
              payoutBenefitPeriod = R.path(
                [policyType, 'payoutBenefitPeriod', 'value'],
                policyDetails,
              ),
            } = cover;

            const value = limitNumber(insuredAmount, limit);

            return (
              <div key={`${policyType}_${i}`} className={styles.lifeCoverItem}>
                <div className={styles.policyType}>
                  <Icon className={styles.icon} />
                  <span>
                    {policyType}
                    {tooltip && (
                      <HelpTooltip
                        toggleClassName={styles.tooltip}
                        content={tooltip}
                        position="right center"
                      />
                    )}
                  </span>
                </div>
                <div className={styles.values}>
                  <Value
                    label=" Insured amount"
                    value={insuredAmount ? `$${prettifyNumber(value)}` : 0}
                  />
                  <Value label="Excess" value={excess} />
                  <Value label="Payment Frequency" value={payoutFrequency} />
                  <Value
                    label="Wait period"
                    value={payoutWaitPeriod}
                    tooltip={getCoverTooltip(
                      'payoutWaitPeriod',
                      policyDetails?.[policyType],
                    )}
                  />
                  <Value
                    label="Benefit period"
                    value={payoutBenefitPeriod}
                    tooltip={getCoverTooltip(
                      'payoutBenefitPeriod',
                      policyDetails?.[policyType],
                    )}
                  />
                </div>
                {!!disclaimer && (
                  <div className={styles.disclaimer}>{disclaimer}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  });
};

const renderHealthInsured = (insured, employerPolicies) => {
  const employerPoliciesHealth = employerPolicies[INSURANCE_TYPES_NAMES.HEALTH];
  const policyDetails = employerPoliciesHealth
    ? employerPoliciesHealth.policyDetails
    : {};

  return (
    <div className={styles.insured}>
      <div className={styles.insuredName}>
        <span>
          <b>Covers</b>
        </span>
      </div>

      <div className={styles.insuredCovers}>
        {insured.map((ins: string, idx) => {
          const tooltip = getCoverTooltip(
            ins,
            policyDetails?.[INSURANCE_TYPES_NAMES.HEALTH],
          );

          const Icon =
            idx > 0
              ? INSURANCE_TYPES_ICONS_SVG_V2[INSURANCE_TYPES_NAMES.LIFE]
              : INSURANCE_TYPES_ICONS_SVG_V2[INSURANCE_TYPES_NAMES.HEALTH];

          return (
            <div key={`insured_${idx}`} className={styles.lifeCoverItem}>
              <div className={styles.policyType}>
                <Icon className={styles.icon} />
                <span>
                  {ins}
                  {tooltip && (
                    <HelpTooltip
                      toggleClassName={styles.tooltip}
                      content={tooltip}
                      position="right center"
                    />
                  )}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LifeCovers = ({ employer, policy, user }) => {
  const { insured = [], policyType } = policy;
  const [open, setOpen] = useState(false);
  const handleTitleClick = useCallback(() => setOpen((prev) => !prev), []);
  const employerPolicies = getEmployerPolicyGrouped(employer?.policies, user);

  const content = useMemo(() => {
    return policyType === INSURANCE_TYPES_NAMES.LIFE
      ? renderLifeInsured(insured, employerPolicies)
      : renderHealthInsured(insured, employerPolicies);
  }, [employerPolicies, insured, policy]);

  if (!insured?.length) {
    return null;
  }

  return (
    <Accordion className={styles.lifeCovers}>
      <Accordion.Title
        active={open}
        index={0}
        onClick={handleTitleClick}
        className={classnames(insured.length < 2 && styles.single)}
      >
        <div className={styles.lifeCoverTitle}>Covers</div>
        <div className={styles.close}>
          <ChevronDown size={20} />
        </div>
      </Accordion.Title>
      <Accordion.Content active={open}>
        <ChevronUp
          className={styles.chevronUp}
          size={20}
          onClick={handleTitleClick}
        />
        {content}
      </Accordion.Content>
    </Accordion>
  );
};

export default LifeCovers;
